import React, { useState, useEffect } from 'react';
import { getData, getUserProfile } from '../firebase';
import { generatePostings, DEFAULT_LEDGERS } from '../ledgers';
import { exportBackupJSON, exportTallyXML, buildDaybook, exportToCSV } from '../exportUtils';

const BRAND = '#0f6e56';
const COLLECTIONS = ['invoices', 'parties', 'receipts', 'payments', 'expenses', 'pettycash', 'cashbook', 'bankbook', 'journal', 'ledgers'];

export default function BackupExport({ user }) {
  const [data, setData] = useState(null);
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    (async () => {
      const results = await Promise.all(COLLECTIONS.map(c => getData(user.uid, c)));
      const obj = {}; COLLECTIONS.forEach((c, i) => obj[c] = results[i]);
      setData(obj);
      getUserProfile(user.uid).then(setBusiness);
      setLoading(false);
    })();
  }, []);

  const posts = data ? generatePostings(data) : [];
  const ledgerDefs = data ? [...DEFAULT_LEDGERS, ...(data.ledgers || []).map(l => ({ name: l.name, head: l.head, group: l.group }))] : DEFAULT_LEDGERS;
  const txnCount = data ? (data.invoices.length + data.receipts.length + data.payments.length + data.expenses.length + data.pettycash.length + data.cashbook.length + data.bankbook.length + data.journal.length) : 0;

  const flash = (t) => { setMsg(t); setTimeout(() => setMsg(''), 4000); };

  const doBackup = () => { exportBackupJSON(data, business); flash('Full JSON backup downloaded — keep it safe.'); };
  const doTally = () => { exportTallyXML(posts, ledgerDefs, business && business.businessName); flash('Tally XML downloaded — import via Gateway of Tally → Import Data.'); };
  const doDaybook = () => { const { headers, rows } = buildDaybook(posts); exportToCSV('finlybooks_daybook.csv', headers, rows); flash('Daybook CSV downloaded.'); };

  const csvChart = () => {
    const used = new Set(); posts.forEach(p => { used.add(p.debit); used.add(p.credit); });
    const rows = ledgerDefs.filter(l => used.has(l.name) || l.system).map(l => [l.name, l.head, l.group]);
    exportToCSV('finlybooks_chart_of_accounts.csv', ['Ledger', 'Under (Head)', 'Nature'], rows);
    flash('Chart of accounts downloaded.');
  };
  const csvParties = () => {
    const rows = (data.parties || []).map(p => [p.name, p.type, p.email || '', p.phone || '', p.city || '', p.country || '', (p.openingBalance || 0)]);
    exportToCSV('finlybooks_parties.csv', ['Name', 'Type', 'Email', 'Phone', 'City', 'Country', 'Opening Balance'], rows);
    flash('Parties (customers & suppliers) downloaded.');
  };
  const csvInvoices = () => {
    const rows = (data.invoices || []).map(i => [i.invoiceNo, i.type, i.date, i.customerName || i.supplierName || '', i.currency || '', i.fxRate || 1,
      (i.fareForeign != null ? i.fareForeign : i.fareAmount) || 0, i.discountForeign != null ? i.discountForeign : (i.discountAmount || 0),
      i.taxForeign != null ? i.taxForeign : (i.taxAmount || 0), i.netForeign != null ? i.netForeign : i.amount, i.amount]);
    exportToCSV('finlybooks_invoices.csv', ['Invoice No', 'Type', 'Date', 'Party', 'Currency', 'FX Rate', 'Fare', 'Discount', 'Tax', 'Net (invoice ccy)', 'Net (base)'], rows);
    flash('Invoices downloaded.');
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 40, color: '#aaa' }}>Loading your books…</div>;

  return (
    <div style={{ maxWidth: 860 }}>
      <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 6 }}>Backup &amp; Export</h2>
      <div style={{ color: '#888', fontSize: 14, marginBottom: 20 }}>Download your complete books for safekeeping, or in formats ready for Tally and other accounting systems. Your data is always yours.</div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(120px,1fr))', gap: 12, marginBottom: 20 }}>
        <Stat label="Transactions" value={txnCount} />
        <Stat label="Postings (legs)" value={posts.length} />
        <Stat label="Parties" value={(data.parties || []).length} />
        <Stat label="Invoices" value={(data.invoices || []).length} />
      </div>

      {msg && <div style={{ background: '#e1f5ee', color: BRAND, borderRadius: 10, padding: '12px 16px', fontSize: 14, marginBottom: 18, fontWeight: 500 }}>{msg}</div>}

      <Card title="1. Full backup (recommended)" desc="A complete JSON snapshot of everything — invoices, parties, receipts, payments, ledgers and more. Use this to keep a safe copy or restore your account later.">
        <button onClick={doBackup} style={btn}>⬇ Download full backup (JSON)</button>
      </Card>

      <Card title="2. Tally-ready export" desc="Ledger masters and accounting vouchers in Tally's XML format. In Tally: Gateway of Tally → Import Data → Vouchers (or Masters), and select this file. Ledgers map to standard Tally groups automatically.">
        <button onClick={doTally} style={btn}>⬇ Download Tally XML</button>
        <div style={{ fontSize: 12, color: '#999', marginTop: 8 }}>Tip: import into a fresh or test company first to review, then into your live company.</div>
      </Card>

      <Card title="3. Universal accountant / ERP pack (CSV)" desc="Standard CSV files any accountant or ERP (Zoho, QuickBooks, Odoo, Sage, Xero) can map. The Daybook holds every double-entry posting — the most portable format there is.">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          <button onClick={doDaybook} style={btn}>⬇ Daybook (all postings)</button>
          <button onClick={csvChart} style={ghost}>Chart of accounts</button>
          <button onClick={csvParties} style={ghost}>Parties</button>
          <button onClick={csvInvoices} style={ghost}>Invoices</button>
        </div>
      </Card>

      <div style={{ fontSize: 12, color: '#999', marginTop: 18, lineHeight: 1.6 }}>
        Note: exports reflect your books at this moment and follow double-entry (every voucher balances). After importing into another system, always review a Trial Balance there to confirm it ties out. This is a data export, not tax or filing advice.
      </div>
    </div>
  );
}

function Card({ title, desc, children }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: 14, padding: 22, marginBottom: 16 }}>
      <div style={{ fontWeight: 700, fontSize: 16 }}>{title}</div>
      <div style={{ fontSize: 13.5, color: '#666', lineHeight: 1.55, margin: '6px 0 14px' }}>{desc}</div>
      {children}
    </div>
  );
}
function Stat({ label, value }) {
  return (
    <div style={{ background: '#f7f8fa', borderRadius: 10, padding: '12px 14px' }}>
      <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: BRAND, marginTop: 3 }}>{value}</div>
    </div>
  );
}
const btn = { padding: '11px 20px', background: BRAND, color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' };
const ghost = { padding: '11px 18px', background: '#fff', color: BRAND, border: `1px solid ${BRAND}`, borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer' };
